import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	acceptUserTypeRequest,
	cancelUserTypeRequest,
	rejectUserTypeRequest,
} from 'Services/Api/_HumanWritten/UserTypeRequestService';
import alertToast from 'Util/ToastifyUtils';
import { userTypeRequestKeys } from './userTypeRequestKeys';

export type UserTypeRequestResolution = 'cancel' | 'reject' | 'accept';

const RESOLVERS: Record<UserTypeRequestResolution, (requestId: string) => Promise<unknown>> = {
	cancel: cancelUserTypeRequest,
	reject: rejectUserTypeRequest,
	accept: acceptUserTypeRequest,
};

const SUCCESS_MESSAGES: Record<UserTypeRequestResolution, string> = {
	cancel: 'Request cancelled',
	reject: 'Request rejected',
	accept: 'Request accepted',
};

/**
 * Cancels (requester side / staff), rejects or accepts (manager+) a pending user type request
 * and refreshes the pending state for the account it was about. Accepting also assigns the type
 * server-side — callers should refetch the user (see RequestUserTypeButton's onAccepted).
 */
export const useResolveUserTypeRequest = (userId?: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ requestId, action }: { requestId: string; action: UserTypeRequestResolution }) =>
			RESOLVERS[action](requestId),
		onSuccess: (_result, { action }) => {
			alertToast(SUCCESS_MESSAGES[action], 'success', 'Success');
			queryClient.invalidateQueries({ queryKey: userTypeRequestKeys.pending(userId) });
		},
		onError: () => {
			alertToast('The request could not be updated. Please refresh and try again.', 'error');
		},
	});
};
